const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface FormattedMarket {
  id: string;
  question: string;
  description?: string;
  category: string;
  endDate?: string;
  outcomes: Array<{
    name: string;
    price: number;
    probability: number;
    tokenId?: string;
  }>;
  volume: number;
  volume24hr: number;
  liquidity: number;
  active: boolean;
  image?: string;
  slug?: string;
}

export interface PriceHistoryPoint {
  t: number;
  p: number;
}

export interface OrderBookEntry {
  price: string;
  size: string;
}

export interface OrderBookData {
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
}

async function proxyRequest(endpoint: string, apiType: 'gamma' | 'clob' = 'gamma', params: Record<string, string> = {}) {
  const searchParams = new URLSearchParams({ endpoint, api: apiType, ...params });

  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/polymarket-proxy?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Polymarket API error: ${response.status} ${errorText}`);
  }

  return response.json();
}

function parseJsonString(value: unknown): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function categorizeMarket(question: string, description?: string, category?: string): string {
  const text = `${question} ${description || ''} ${category || ''}`.toLowerCase();

  if (text.includes('crypto') || text.includes('bitcoin') || text.includes('ethereum') || text.includes('btc') || text.includes('eth') || text.includes('solana') || text.includes('defi')) {
    return 'crypto';
  }
  if (text.includes('trump') || text.includes('biden') || text.includes('election') || text.includes('president') || text.includes('politic') || text.includes('congress') || text.includes('senate') || text.includes('governor') || text.includes('democrat') || text.includes('republican')) {
    return 'politics';
  }
  if (text.includes('nba') || text.includes('nfl') || text.includes('soccer') || text.includes('football') || text.includes('sport') || text.includes('ufc') || text.includes('mlb') || text.includes('nhl') || text.includes('tennis') || text.includes('f1') || text.includes('formula')) {
    return 'sports';
  }
  if (text.includes('economy') || text.includes('gdp') || text.includes('inflation') || text.includes('stock') || text.includes('fed') || text.includes('interest rate') || text.includes('recession') || text.includes('tariff')) {
    return 'economy';
  }
  if (text.includes('tech') || text.includes(' ai ') || text.includes('apple') || text.includes('google') || text.includes('microsoft') || text.includes('openai') || text.includes('artificial intelligence')) {
    return 'tech';
  }

  return 'general';
}

function formatMarketFromApi(raw: Record<string, unknown>): FormattedMarket | null {
  const question = (raw.question as string) || '';
  const conditionId = (raw.conditionId as string) || (raw.condition_id as string) || (raw.id as string) || '';
  const closed = raw.closed === true;
  const active = raw.active === true;
  const archived = raw.archived === true;

  if (closed || archived || !active) return null;

  const outcomesRaw = parseJsonString(raw.outcomes || raw.outcome);
  const pricesRaw = parseJsonString(raw.outcomePrices || raw.outcome_prices);
  const tokenIdsRaw = parseJsonString(raw.clobTokenIds || raw.clob_token_ids);

  const outcomes = outcomesRaw.map((name, index) => {
    const priceStr = pricesRaw[index] || '0';
    const price = parseFloat(priceStr);
    return {
      name,
      price: isNaN(price) ? 0 : price,
      probability: isNaN(price) ? 0 : price * 100,
      tokenId: tokenIdsRaw[index] || undefined,
    };
  });

  const hasValidPrices = outcomes.some(o => o.price > 0 && o.price < 1);
  if (!hasValidPrices && outcomes.length > 0) return null;

  const volume = parseFloat(String(raw.volumeNum || raw.volume || '0'));
  const volume24hr = parseFloat(String(raw.volume24hr || '0'));
  const liquidity = parseFloat(String(raw.liquidityNum || raw.liquidity || '0'));

  const endDate = (raw.endDateIso as string) || (raw.end_date_iso as string) || (raw.endDate as string) || '';
  if (endDate) {
    const end = new Date(endDate);
    if (end.getTime() < Date.now()) return null;
  }

  const category = categorizeMarket(
    question,
    raw.description as string | undefined,
    raw.category as string | undefined
  );

  return {
    id: conditionId,
    question,
    description: raw.description as string | undefined,
    category,
    endDate: endDate || undefined,
    outcomes,
    volume,
    volume24hr,
    liquidity,
    active: true,
    image: (raw.image as string) || (raw.icon as string) || undefined,
    slug: raw.slug as string | undefined,
  };
}

function formatEventFromApi(raw: Record<string, unknown>): FormattedMarket[] {
  const markets = (raw.markets as Record<string, unknown>[]) || [];
  const eventImage = (raw.image as string) || (raw.icon as string) || '';

  const results: FormattedMarket[] = [];

  for (const m of markets) {
    const formatted = formatMarketFromApi(m);
    if (formatted) {
      if (!formatted.image && eventImage) {
        formatted.image = eventImage;
      }
      results.push(formatted);
    }
  }

  return results;
}

export async function fetchAllPolymarketMarkets(params?: {
  limit?: number;
}): Promise<FormattedMarket[]> {
  const limit = params?.limit || 150;

  try {
    const eventsData = await proxyRequest('/events', 'gamma', {
      active: 'true',
      closed: 'false',
      limit: String(limit),
      order: 'liquidity',
      ascending: 'false',
    });

    const allMarkets: FormattedMarket[] = [];

    if (Array.isArray(eventsData)) {
      for (const event of eventsData) {
        const formatted = formatEventFromApi(event as Record<string, unknown>);
        allMarkets.push(...formatted);
      }
    }

    if (allMarkets.length < 20) {
      const marketsData = await proxyRequest('/markets', 'gamma', {
        active: 'true',
        closed: 'false',
        limit: String(limit),
        order: 'liquidityNum',
        ascending: 'false',
      });

      if (Array.isArray(marketsData)) {
        const existingIds = new Set(allMarkets.map(m => m.id));
        for (const raw of marketsData) {
          const formatted = formatMarketFromApi(raw as Record<string, unknown>);
          if (formatted && !existingIds.has(formatted.id)) {
            allMarkets.push(formatted);
            existingIds.add(formatted.id);
          }
        }
      }
    }

    allMarkets.sort((a, b) => {
      if (b.volume24hr !== a.volume24hr) return b.volume24hr - a.volume24hr;
      return b.liquidity - a.liquidity;
    });

    return allMarkets;
  } catch (error) {
    console.error('Error fetching Polymarket markets:', error);
    throw error;
  }
}

export async function fetchPriceHistory(
  tokenId: string,
  interval: string = 'max',
  fidelity: number = 1440
): Promise<PriceHistoryPoint[]> {
  try {
    const data = await proxyRequest('/prices-history', 'clob', {
      market: tokenId,
      interval,
      fidelity: String(fidelity),
    });
    return (data?.history || data || []) as PriceHistoryPoint[];
  } catch (error) {
    console.error('Error fetching price history:', error);
    return [];
  }
}

export async function fetchOrderBook(tokenId: string): Promise<OrderBookData> {
  try {
    const data = await proxyRequest('/book', 'clob', {
      token_id: tokenId,
    });
    return {
      bids: (data?.bids || []) as OrderBookEntry[],
      asks: (data?.asks || []) as OrderBookEntry[],
    };
  } catch (error) {
    console.error('Error fetching order book:', error);
    return { bids: [], asks: [] };
  }
}

function extractOutcomePrices(data: Record<string, unknown>): Array<{ name: string; price: number }> | null {
  const outcomesRaw = parseJsonString(data.outcomes || data.outcome);
  const pricesRaw = parseJsonString(data.outcomePrices || data.outcome_prices);
  if (outcomesRaw.length === 0) return null;

  const outcomes = outcomesRaw.map((name: string, index: number) => {
    const price = parseFloat(pricesRaw[index] || '0');
    return { name, price: isNaN(price) ? 0 : price };
  });

  return outcomes.some((o) => o.price > 0) ? outcomes : null;
}

export async function fetchMarketPricesByIds(
  polymarketIds: string[]
): Promise<Map<string, Array<{ name: string; price: number }>>> {
  const result = new Map<string, Array<{ name: string; price: number }>>();
  const unique = [...new Set(polymarketIds)];

  const fetches = unique.map(async (id) => {
    try {
      let data = await proxyRequest(`/markets/${id}`, 'gamma');

      if (Array.isArray(data)) {
        data = data[0];
      }

      if (data) {
        const outcomes = extractOutcomePrices(data as Record<string, unknown>);
        if (outcomes) {
          result.set(id, outcomes);
          return;
        }
      }

      const arrData = await proxyRequest('/markets', 'gamma', { condition_id: id });
      const market = Array.isArray(arrData) ? arrData[0] : arrData;
      if (market) {
        const outcomes = extractOutcomePrices(market as Record<string, unknown>);
        if (outcomes) {
          result.set(id, outcomes);
        }
      }
    } catch {
      // skip failed fetches
    }
  });

  await Promise.all(fetches);
  return result;
}
